import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import Card from './Card'
import HabitManager from './HabitManager'
import { getCustomHabitCategories } from '../services/customHabitCategories'
import { useOrganizationId } from '../hooks/useOrganizationId'

/**
 * WellnessHabitsSection Component
 * Displays separate cards for each habit type (diet, exercise, sleep, and custom categories)
 */
const WellnessHabitsSection = ({
  diet = [],
  exercise = [],
  sleep = [],
  customHabits = {},
  onDietChange,
  onExerciseChange,
  onSleepChange,
  onCustomHabitChange,
}) => {
  const organizationId = useOrganizationId()
  const [customCategories, setCustomCategories] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (organizationId) {
      loadCustomCategories()
    }
  }, [organizationId])

  const loadCustomCategories = async () => {
    setLoading(true)
    const result = await getCustomHabitCategories(organizationId)
    if (result.success) {
      setCustomCategories(result.data)
    }
    setLoading(false)
  }

  // Standard habit cards
  const standardCards = [
    {
      id: 'diet',
      label: 'Dietary Habits',
      habits: diet,
      onChange: onDietChange,
      placeholder: 'Add a dietary habit...',
      color: 'primary',
    },
    {
      id: 'exercise',
      label: 'Exercise Routine',
      habits: exercise,
      onChange: onExerciseChange,
      placeholder: 'Add an exercise habit...',
      color: 'primary',
    },
    {
      id: 'sleep',
      label: 'Sleep Patterns',
      habits: sleep,
      onChange: onSleepChange,
      placeholder: 'Add a sleep pattern...',
      color: 'primary',
    },
  ]

  // Custom habit cards
  const customCards = customCategories.map((category) => ({
    id: category.id,
    label: category.name,
    habits: customHabits[category.id] || [],
    onChange: (habits) => onCustomHabitChange(category.id, habits),
    placeholder: `Add a ${category.name.toLowerCase()} habit...`,
    color: 'primary',
    description: category.description,
  }))

  const allCards = [...standardCards, ...customCards]

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Lifestyle & Wellness Habits
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Track habits and patterns. Tag habits to address during care planning.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Loading habit categories...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allCards.map((card) => (
            <Card
              key={card.id}
              variant="soft"
              className="p-4 hover:shadow-lg transition-shadow"
            >
              <HabitManager
                label={card.label}
                habits={card.habits}
                onChange={card.onChange}
                placeholder={card.placeholder}
                required={false}
              />
              {card.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                  {card.description}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {allCards.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No habit categories available.</p>
          <p className="text-sm mt-1">
            Contact your organization admin to add custom habit categories.
          </p>
        </div>
      )}
    </div>
  )
}

export default WellnessHabitsSection

